import React from "react";
import { withStyles, Theme } from "@material-ui/core/styles";
import MUIDataTableBase, { MUIDataTableOptions, MUIDataTableProps } from "mui-datatables";
import ColumnCrudTableType from "./columnCrudTableType";
import * as _ from "lodash";
import { MuiThemeProvider } from "@material-ui/core/styles";
import { withTheme } from "@material-ui/core/styles";
import EstadoVazioTabela from "../Table/estadoVazio";

const textOptions = {
  textLabels: {
    body: {
      // o d.ts do mui-datatables declara noMatch como string, mas o runtime aceita ReactNode
      noMatch: (<EstadoVazioTabela />) as unknown as string,
      toolTip: "Ordenar",
    },
    pagination: {
      next: "Próxima página",
      previous: "Página anterior",
      rowsPerPage: "Linhas por página:",
      displayRows: "de",
    },
    toolbar: {
      search: "Procurar",
      downloadCsv: "Download CSV",
      print: "Imprimir",
      viewColumns: "Exibir Colunas",
      filterTable: "Filtrar",
    },
    filter: {
      all: "TODOS",
      title: "FILTROS",
      reset: "RESET",
    },
    viewColumns: {
      title: "Exibir Colunas",
      titleAria: "Exibir/Esconder Colunas",
    },
    selectedRows: {
      text: "linha(s) selecionadas",
      delete: "Deletar",
      deleteAria: "Deletar linhas selecionadas",
    },
  },
};

export interface RowType {
  index: number;
  dataIndex: number;
}

export interface RowDeleted {
  data: Array<RowType>;
  lookup: { [key: number]: boolean };
}

interface Props {
  classes?: any;
  data: (object | string[] | number[])[];
  columns: Array<ColumnCrudTableType>;
  titulo: string;
  onRowsSelect?: (currentRowsSelected: Array<RowType>, allRowsSelected: Array<RowType>) => void;
  onAbrirRegistro?: (dataIndex: number) => void;
  options?: {
    filterType?: "dropdown" | "checkbox" | "multiselect" | "textField";
    responsive?: "stacked" | "scrollMaxHeight" | "scrollFullHeight" | undefined;
    print?: boolean;
    download?: boolean;
    viewColumns?: boolean;
    filter?: boolean;
    searchable?: boolean;
    rowsPerPage?: number;
    page?: number;
    onRowsDelete?: MUIDataTableOptions["onRowsDelete"];
    selectableRows?: "none" | "multiple" | "single" | undefined;
    isRowSelectable?: (dataIndex: number) => boolean;
  };
  themeTable?: Object;
  selecaoMultipla?: boolean;
  colunasFixas?: boolean;
  theme: Theme;
}

type DefaultProps = {
  options: {
    filterType: "dropdown" | "checkbox" | "multiselect" | "textField";
    responsive: "stacked" | "scrollMaxHeight" | "scrollFullHeight" | undefined;
    print: boolean;
    download: boolean;
    viewColumns?: boolean;
    filter: boolean;
    rowsPerPage: number;
    page: number;
    selectableRows: "none" | "multiple" | "single" | undefined;
    isRowSelectable?: (dataIndex: number) => boolean;
  };
};

export const styles = (theme: any) => ({
  areaTabela: {
    [theme.breakpoints.down("sm")]: {
      // O botao flutuante de acoes do Wrapper e fixo no rodape e cobria a paginacao
      paddingBottom: 96,
      // o empilhado da biblioteca nao separa os registros: eles ficam colados uns nos outros.
      // border em <tr> e ignorado pelo navegador (computa 0px ate inline), por isso outline
      "& tbody tr": {
        outline: "1px solid " + theme.palette.divider,
        outlineOffset: -1,
        borderRadius: 4,
        marginBottom: 12,
        padding: "4px 8px",
      },
      // a celula de selecao vinha como bloco de largura total acima de cada registro
      "& tbody td[class*='MUIDataTableSelectCell']": {
        float: "right",
        width: "auto",
        padding: 0,
        borderBottom: "none",
      },
    },
  },
});

const MUIDataTable = MUIDataTableBase as React.ComponentType<MUIDataTableProps & { ref?: React.Ref<any> }>;

const TECLAS_SELECAO = ["Enter", " ", "Spacebar"];
const TECLAS_ESPACO = [" ", "Spacebar"];

const primeiraColunaDoCabecalho = (tabela: Element | null) =>
  tabela && tabela.querySelector("thead [role=button]");

const CONTROLES_DO_CABECALHO = "thead input[type=checkbox], thead [role=button]:not([aria-hidden=true])";

const navegaNoCabecalho = (evento: React.KeyboardEvent<HTMLDivElement>) => {
  const alvo = evento.target as HTMLElement;
  const tabela = alvo.closest("thead") && alvo.closest("table");
  if (!tabela) {
    return;
  }
  let destino: HTMLElement | null = null;
  if (evento.key === "ArrowDown") {
    destino = tabela.querySelector("tbody tr[tabindex]");
  } else if (evento.key === "ArrowLeft" || evento.key === "ArrowRight") {
    const controles = Array.from(tabela.querySelectorAll(CONTROLES_DO_CABECALHO)) as HTMLElement[];
    destino = controles[controles.indexOf(alvo) + (evento.key === "ArrowRight" ? 1 : -1)] || null;
  }
  if (destino) {
    evento.preventDefault();
    destino.focus();
  }
};

// o primeiro Tab caia na ordenacao de Login e o Enter invertia a ordem; o cabecalho fica nas setas
// a linha ja seleciona com Espaco; o checkbox dela seria uma segunda parada do Tab por registro
const tiraDoTabOQueNaoERegistro = (area: HTMLElement) => {
  area.querySelectorAll(`${CONTROLES_DO_CABECALHO}, tbody input[type=checkbox]`).forEach((controle) => {
    (controle as HTMLElement).tabIndex = -1;
  });
};

const navegaPorTeclado = (
  evento: React.KeyboardEvent<HTMLTableRowElement>,
  dataIndex: number,
  abrirRegistro?: (dataIndex: number) => void
) => {
  const linha = evento.currentTarget;
  if (evento.target !== linha) {
    return;
  }
  if (evento.key === "ArrowDown" || evento.key === "ArrowUp") {
    const anterior = linha.previousElementSibling || primeiraColunaDoCabecalho(linha.closest("table"));
    const destino = evento.key === "ArrowDown" ? linha.nextElementSibling : anterior;
    evento.preventDefault();
    if (destino) {
      (destino as HTMLElement).focus();
    }
  } else if (abrirRegistro && evento.key === "Enter") {
    evento.preventDefault();
    abrirRegistro(dataIndex);
  } else if ((abrirRegistro ? TECLAS_ESPACO : TECLAS_SELECAO).includes(evento.key)) {
    const checkbox = linha.querySelector("input[type=checkbox]") as HTMLInputElement | null;
    evento.preventDefault();
    if (checkbox && !checkbox.disabled) {
      checkbox.click();
    }
  }
};

class Table extends React.Component<Props> {
  private tabela: any = null;
  private area: HTMLDivElement | null = null;

  componentDidMount() {
    this.ajustaParadasDoTab();
  }

  componentDidUpdate() {
    this.ajustaParadasDoTab();
  }

  private ajustaParadasDoTab = () => {
    if (this.area) {
      tiraDoTabOQueNaoERegistro(this.area);
    }
  };

  // O checkbox do cabecalho do mui-datatables seleciona todo o displayData; restringe a pagina exibida
  private estaNaPaginaAtual = (dataIndex: number) => {
    const estado = this.tabela && this.tabela.state;
    if (!estado || !estado.displayData) {
      return true;
    }
    const inicio = estado.page * estado.rowsPerPage;
    return estado.displayData
      .slice(inicio, inicio + estado.rowsPerPage)
      .some((linha: { dataIndex: number }) => linha.dataIndex === dataIndex);
  };

  static defaultProps: DefaultProps = {
    options: {
      filterType: "dropdown",
      responsive: "stacked",
      print: false,
      download: false,
      viewColumns: false,
      filter: false,
      rowsPerPage: 10,
      page: 0,
      selectableRows: "multiple",
      isRowSelectable: (dataIndex: number) => true,
    },
  };

  shouldComponentUpdate(props: Props) {
    let retorno = true;
    if (this.props.data.length === 0 || props.data.length === 0) {
      return true;
    }
    if (this.props.data.length === props.data.length) {
      if (_.isEqual(this.props.data, props.data)) {
        return false;
      }
    }

    return retorno;
  }

  render() {
    const {
      classes,
      columns,
      data,
      titulo,
      options,
      onRowsSelect,
      theme,
      themeTable = {},
      selecaoMultipla = true,
      colunasFixas = false,
    } = this.props;
    // withTheme injeta a MESMA instancia de tema usada pelo app inteiro. Mutar theme.overrides
    // aqui apagava as cores/fontes de tabela em todas as telas (ex.: Central de Alertas), so
    // voltando com hard refresh. Trabalhar sobre uma copia local (shallow + novo overrides).
    // Nao usar cloneDeep: o tema tem funcoes (spacing, breakpoints) que seriam destruidas.
    const base = ((theme as any).overrides || {}) as any;
    const telaPequena = theme.breakpoints.down("sm");
    const telaMinima = theme.breakpoints.down("xs");
    const localTheme = { ...theme, overrides: { ...base } } as any;
    // o mui-datatables ja envolve o cabecalho num span role="button" com o texto da coluna;
    // a seta do TableSortLabel vira um segundo controle focavel e sem nome acessivel
    localTheme.props = {
      ...(theme as any).props,
      MuiTableSortLabel: { "aria-hidden": true, tabIndex: -1 },
    };
    // MESCLAR (nao substituir) os overrides de tabela do tema do app: assim as tabelas dos
    // CRUDs herdam a mesma tipografia/cor da tabela da Central de Alertas (fontSize 14,
    // cabecalho azul, corpo navy) e aqui so entram os ajustes de densidade do CRUD.
    localTheme.overrides["MuiTableCell"] = {
      ...base.MuiTableCell,
      root: {
        ...(base.MuiTableCell || {}).root,
        padding: "4px 2px 4px 2px",
        [telaPequena]: { padding: "4px 2px 4px 10px" },
      },
      body: {
        ...(base.MuiTableCell || {}).body,
        overflow: "overlay",
        ...(colunasFixas ? { wordBreak: "break-word" } : {}),
      },
      ...themeTable,
    };
    localTheme.overrides["MuiTable"] = {
      root: {
        marginBottom: 0,
        ...(colunasFixas ? { tableLayout: "fixed" } : {}),
      },
    };
    localTheme.overrides["MUIDataTableHeadCell"] = { ...base.MUIDataTableHeadCell };
    // o padrao do mui-datatables divide a linha empilhada meio a meio e espreme o valor
    localTheme.overrides["MUIDataTableBodyCell"] = {
      ...base.MUIDataTableBodyCell,
      cellStackedSmall: {
        fontWeight: "bold",
        [telaPequena]: { width: "40%", paddingRight: 8 },
      },
      responsiveStackedSmall: {
        [telaPequena]: { width: "60%" },
      },
    };
    localTheme.overrides["MUIDataTableSelectCell"] = {
      ...base.MUIDataTableSelectCell,
      root: {
        ...(base.MUIDataTableSelectCell || {}).root,
        [telaPequena]: { display: "block", width: "100%", borderBottom: "solid 1px rgba(0, 0, 0, 0.1)" },
      },
    };
    const toolbar = base.MUIDataTableToolbar || {};
    const paginacao = base.MUIDataTablePagination || {};
    // A lupa absoluta do tema global cai sobre a primeira linha quando a tabela empilha
    localTheme.overrides["MUIDataTableToolbar"] = {
      ...toolbar,
      actions: {
        ...toolbar.actions,
        [telaPequena]: { position: "static", top: "auto", right: "auto", textAlign: "right", paddingBottom: 8 },
      },
    };
    localTheme.overrides["MUIDataTablePagination"] = {
      ...paginacao,
      root: { ...paginacao.root, [telaPequena]: { "&:last-child": { padding: "0px 8px" } } },
      toolbar: {
        ...paginacao.toolbar,
        [telaMinima]: { flexWrap: "wrap", justifyContent: "center", height: "auto", minHeight: "auto", paddingLeft: 0 },
      },
      selectRoot: { ...paginacao.selectRoot, [telaMinima]: { marginLeft: 4, marginRight: 8 } },
    };
    localTheme.overrides["MUIDataTableBodyRow"] = {
      ...base.MUIDataTableBodyRow,
      root: {
        ...(base.MUIDataTableBodyRow || {}).root,
        "&:focus": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
      },
      responsiveStacked: {
        [telaPequena]: {
          display: "block",
          marginBottom: 12,
          border: "solid 2px rgba(0, 0, 0, 0.15)",
          borderRadius: 4,
        },
      },
    };
    localTheme.overrides["MUIDataTableBodyCell"] = {
      ...localTheme.overrides["MUIDataTableBodyCell"],
      stackedCommon: {
        [telaPequena]: {
          height: "auto",
          minHeight: 32,
          paddingTop: 6,
          paddingBottom: 6,
          whiteSpace: "normal",
          wordBreak: "break-word",
          verticalAlign: "top",
        },
      },
    };
    const isRowSelectable = options!.isRowSelectable || (() => true);
    // o checkbox do cabecalho so existe em "multiple"; sem acao em massa ele seleciona
    // registros que ninguem consegue usar, mas a selecao de um registro abre a edicao
    const selectableRows =
      !selecaoMultipla && options!.selectableRows !== "none" ? "single" : options!.selectableRows;
    return (
      <div
        className={classes.areaTabela}
        ref={(area) => {
          this.area = area;
        }}
        onKeyDown={navegaNoCabecalho}
      >
        <MuiThemeProvider theme={localTheme}>
          <MUIDataTable
            ref={(tabela: any) => {
              this.tabela = tabela;
            }}
            title={titulo}
            data={data}
            columns={columns}
            options={{
              ...options,
              ...textOptions,
              selectableRows,
              onRowsSelect,
              isRowSelectable: (dataIndex: number) => isRowSelectable(dataIndex) && this.estaNaPaginaAtual(dataIndex),
              onTableChange: this.ajustaParadasDoTab,
              setTableProps: () => ({ tabIndex: -1 }),
              // shouldComponentUpdate segura o render quando so as props de callback mudam
              setRowProps: (linha: any, dataIndex: number) => ({
                tabIndex: 0,
                onKeyDown: (evento: React.KeyboardEvent<HTMLTableRowElement>) =>
                  navegaPorTeclado(evento, dataIndex, this.props.onAbrirRegistro),
              }),
              customToolbarSelect: (selectedRows, displayData, setSelectedRows) => null,
            }}
          />
        </MuiThemeProvider>
      </div>
    );
  }
}

const TableCrud = withStyles(styles as any)(Table);

export default withTheme(TableCrud);
